import { Table, Text, Box, Group } from '@mantine/core';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { VOYAGE_VIBE_RESULTS } from '@queries/voyage-vibe-state';
import type { GeoJSONFeature } from 'mapbox-gl';
import { COLOR_RANKING } from '@lib/map-results';

type Visibility = 'visible' | 'none';

interface Props {
  feature: GeoJSONFeature;
  allFeatures: GeoJSONFeature[];
  layerStates: {
    unesco: Visibility;
    wineRegion: Visibility;
    countries: Visibility;
    cities: Visibility;
  };
}

function MiniTable({ rows }: { rows: { label: string; value: string | number | null | undefined }[] }) {
  return (
    <Table fz="xs" verticalSpacing={2} withColumnBorders style={{ flex: 1 }}>
      <Table.Tbody>
        {rows.map((row) => (
          <Table.Tr key={row.label}>
            <Table.Td fw={600} w={130}>
              {row.label}
            </Table.Td>
            <Table.Td>{row.value ?? '—'}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function DebugNutsResult({ feature, allFeatures, layerStates }: Props) {
  const { tripProfileId } = useParams();
  const props = feature.properties ?? {};
  const nutsId: string = (props.nuts_id as string) ?? '';
  const overallRank: number = Number(props.overall_rank ?? 0);
  const rankLabel = COLOR_RANKING[overallRank - 1]?.text ?? '—';

  const { data, loading } = useQuery(VOYAGE_VIBE_RESULTS, {
    variables: { tripProfileId: tripProfileId ?? '' },
    skip: !tripProfileId,
  });

  const result = data?.findVoyageVibeResults?.find((r) => r.nutId === nutsId);
  const lv = (v: number | null | undefined) => v?.toFixed(3) ?? (loading ? '…' : '—');

  const leftRows = [
    { label: 'NUTS ID', value: nutsId },
    { label: 'Name', value: (props.name_latn as string) ?? result?.nameDisplay ?? '—' },
    { label: 'Country', value: props.country_name as string },
    { label: 'Overall Rank', value: `${overallRank} — ${rankLabel}` },
    { label: 'Physical Rank', value: props.physical_rank as string },
    { label: 'Cultural Rank', value: props.cultural_rank as string },
    { label: 'History Rank', value: props.history_rank as string },
    { label: 'Score (attr)', value: result?.scoreActAttr?.toFixed(4) ?? (loading ? '…' : '—') },
    { label: 'Result Sum', value: result?.resultSum?.toFixed(4) ?? (loading ? '…' : '—') },
    { label: 'UNESCO', value: layerStates.unesco },
    { label: 'Wine Regions', value: layerStates.wineRegion },
    { label: 'Countries', value: layerStates.countries },
    { label: 'Cities', value: layerStates.cities },
  ];

  const rightRows = [
    { label: 'City Tour', value: lv(result?.resultCityTour) },
    { label: 'Outdoor Adventure', value: lv(result?.resultOutdoorAdventure) },
    { label: 'Museum/Gallery', value: lv(result?.resultVisitMuseumGallery) },
    { label: 'Walking', value: lv(result?.resultWalkingMeanderingLocally) },
    { label: 'Outdoor Recreation', value: lv(result?.resultOutdoorRecreationExploration) },
    { label: 'Performance/Festival', value: lv(result?.resultAttendPerformanceFestival) },
    { label: 'Shopping', value: lv(result?.resultShopping) },
    { label: 'Physical Adventure', value: lv(result?.physicalAdventure) },
    { label: 'Interactivity', value: lv(result?.interactivityLocals) },
    { label: 'Learning', value: lv(result?.learning) },
  ];

  const uniqueLayers = [...new Map(allFeatures.map((f) => [f.layer?.id, f.layer?.id])).values()].filter(Boolean);

  return (
    <Box>
      <Text fw={700} fz="xs" c="var(--mantine-color-gray-6)" mb={4}>
        NUTS Debug — Alt+Click
      </Text>
      <Group align="flex-start" gap="xs" wrap="nowrap">
        <MiniTable rows={leftRows} />
        <MiniTable rows={rightRows} />
        <Box style={{ flex: 1 }}>
          <Text fw={700} fz="xs" c="var(--mantine-color-gray-6)" mb={4}>
            Rendered Layers at Point
          </Text>
          <Table fz="xs" verticalSpacing={2} withColumnBorders>
            <Table.Tbody>
              {uniqueLayers.map((id) => (
                <Table.Tr key={id}>
                  <Table.Td>{id}</Table.Td>
                </Table.Tr>
              ))}
              {uniqueLayers.length === 0 && (
                <Table.Tr>
                  <Table.Td c="dimmed">none</Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Box>
      </Group>
    </Box>
  );
}

export default DebugNutsResult;
